import { IStage } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import {
   addStageToGroupUtil,
   addTagToGroupUtil,
   extractStagesAndTagsByGroup,
   removeStageFromAllGroupsUtil,
   removeStageFromGroupUtil,
   removeTagFromAllGroupsUtil,
   removeTagFromGroupUtil,
   isNestedStopKey,
} from '@forwardflow/features/dnd-stage-mapping/utils'
import { IStageMapGroup as TDndGroup } from '@forwardflow/features/dnd-stage-mapping/interfaces'
import { BUCKETS, REQUIRED_BUCKETS, TBucketKey } from './buckets'

/*
 * The drag-and-drop feature declares its own copy of `IStageMapGroup` whose
 * tags are `IStage[]`; the store's copy types them as `ITag[]`. Same shape at
 * runtime, so the two casts below bridge them (the DnD step does the same).
 */
const dnd = (groups: IStageMapGroup[]) => groups as unknown as TDndGroup[]
const back = (groups: TDndGroup[]) => groups as unknown as IStageMapGroup[]

/** An item the customer can pick: a FUB stage or a FUB tag. */
export type TMappedItem = IStage & { type: 'stage' | 'tag' }

/** Stable key for an item, so a tag and a stage with the same id never clash. */
export const itemKey = (item: Pick<TMappedItem, 'id' | 'type'>) =>
   `${item.type}:${item.id}`

/** The timeline of the first nurturing group, which this screen edits. */
export const nurturingTimeline = (groups: IStageMapGroup[]) =>
   groups.find((g) => g.group === 'ff-nurturing')?.timeline

const timelineFor = (groups: IStageMapGroup[], key: TBucketKey) =>
   key === 'ff-nurturing' ? nurturingTimeline(groups) : undefined

/** Stages and tags currently mapped to a bucket. */
export const itemsIn = (
   groups: IStageMapGroup[],
   key: TBucketKey
): TMappedItem[] => {
   const { stages, tags } = extractStagesAndTagsByGroup(key, dnd(groups))
   return [
      ...stages
         .filter((s) => s?.id && s?.name?.trim())
         .map((s) => ({ ...s, type: 'stage' as const })),
      ...tags
         .filter((t) => t?.name?.trim())
         .map((t) => ({ ...t, id: t.id || t.name, type: 'tag' as const })),
   ]
}

/** Every item mapped anywhere, keyed by `itemKey`. */
export const assignedKeys = (groups: IStageMapGroup[]): Set<string> =>
   new Set(BUCKETS.flatMap((b) => itemsIn(groups, b.key).map(itemKey)))

/** Add an item to a bucket, moving it out of any other bucket first. */
export const addItem = (
   groups: IStageMapGroup[],
   item: TMappedItem,
   key: TBucketKey
): IStageMapGroup[] => {
   const timeline = timelineFor(groups, key)
   if (item.type === 'tag') {
      const cleared = removeTagFromAllGroupsUtil(dnd(groups), item.id)
      return back(addTagToGroupUtil(cleared, item, key, timeline))
   }
   const cleared = removeStageFromAllGroupsUtil(dnd(groups), item.id)
   return back(addStageToGroupUtil(cleared, item, key, timeline))
}

/** Remove an item from a bucket. */
export const removeItem = (
   groups: IStageMapGroup[],
   item: TMappedItem,
   key: TBucketKey
): IStageMapGroup[] => {
   const timeline = timelineFor(groups, key)
   return back(
      item.type === 'tag'
         ? removeTagFromGroupUtil(dnd(groups), item.id, key, timeline)
         : removeStageFromGroupUtil(dnd(groups), item.id, key, timeline)
   )
}

/**
 * The suggestions for one bucket, flattened from the suggested-mappings
 * response (same shape as the saved groups), minus anything already mapped.
 */
export const suggestionsFor = (
   suggestions: IStageMapGroup[] | null | undefined,
   groups: IStageMapGroup[],
   key: TBucketKey
): TMappedItem[] => {
   if (!suggestions?.length) return []
   const taken = assignedKeys(groups)
   const seen = new Set<string>()
   const out: TMappedItem[] = []
   const push = (item: TMappedItem) => {
      const k = itemKey(item)
      if (!item.name?.trim() || taken.has(k) || seen.has(k)) return
      seen.add(k)
      out.push(item)
   }
   const collect = (stages?: IStage[], tags?: IStage[]) => {
      stages?.forEach((s) => {
         const id = s?.id?.toString()
         if (id) push({ id, name: s.name, description: '', type: 'stage' })
      })
      tags?.forEach((t) => {
         if (t?.name)
            push({ id: t.name, name: t.name, description: '', type: 'tag' })
      })
   }
   suggestions.forEach((mapping) => {
      if (mapping.group === key) collect(mapping.stages, mapping.tags)
      if (mapping.group === 'ff-stopped' && isNestedStopKey(key)) {
         mapping.types
            ?.filter((t) => t.key === key)
            .forEach((t) => collect(t.stages, t.tags))
      }
   })
   return out
}

/** Apply every suggestion that is not already mapped somewhere. */
export const applyAllSuggestions = (
   groups: IStageMapGroup[],
   suggestions: IStageMapGroup[] | null | undefined
): IStageMapGroup[] =>
   BUCKETS.reduce(
      (acc, bucket) =>
         suggestionsFor(suggestions, acc, bucket.key).reduce(
            (inner, item) => addItem(inner, item, bucket.key),
            acc
         ),
      groups
   )

export const hasAnySelection = (groups: IStageMapGroup[]) =>
   assignedKeys(groups).size > 0

/** Required if the saved group says so, else per `REQUIRED_BUCKETS`. */
export const isBucketRequired = (
   groups: IStageMapGroup[],
   key: TBucketKey
): boolean => {
   if (isNestedStopKey(key)) {
      const sub = groups
         .find((g) => g.group === 'ff-stopped')
         ?.types?.find((t) => t.key === key)
      if (sub && typeof sub.isMandatory === 'boolean') return sub.isMandatory
   } else {
      const group = groups.find((g) => g.group === key)
      if (group && typeof group.isMandatory === 'boolean')
         return group.isMandatory
   }
   return REQUIRED_BUCKETS.includes(key)
}

/** Required buckets that are still empty. */
export const missingRequired = (groups: IStageMapGroup[]) =>
   BUCKETS.filter(
      (b) => isBucketRequired(groups, b.key) && itemsIn(groups, b.key).length === 0
   )

/** Readable "A, B and C". */
export const joinNames = (items: TMappedItem[]) => {
   const names = items.map((i) => i.name)
   if (names.length <= 1) return names.join('')
   return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/* ---------- Stop-only view helpers ---------- */

/** The buckets that stop Forward Flow, in display order. */
export const STOP_BUCKETS = BUCKETS.filter((b) => b.phase === 'stop')

export type TStopItem = TMappedItem & { bucket: TBucketKey; bucketTitle: string }

/** Every stage and tag mapped to any stop bucket. */
export const stopItems = (groups: IStageMapGroup[]): TStopItem[] =>
   STOP_BUCKETS.flatMap((b) =>
      itemsIn(groups, b.key).map((item) => ({
         ...item,
         bucket: b.key,
         bucketTitle: b.title,
      }))
   )

/** Suggested stop stages and tags not mapped anywhere yet. */
export const suggestedStops = (
   suggestions: IStageMapGroup[] | null | undefined,
   groups: IStageMapGroup[]
): TStopItem[] =>
   STOP_BUCKETS.flatMap((b) =>
      suggestionsFor(suggestions, groups, b.key).map((item) => ({
         ...item,
         bucket: b.key,
         bucketTitle: b.title,
      }))
   )

/** Add a stop item; suggestions keep their bucket, manual picks go to "Anything else". */
export const addStop = (
   groups: IStageMapGroup[],
   item: TMappedItem,
   bucket: TBucketKey = 'ff-other-stop'
): IStageMapGroup[] => addItem(groups, item, bucket)

/** Remove a stop item from wherever it is. */
export const removeStop = (
   groups: IStageMapGroup[],
   item: TMappedItem
): IStageMapGroup[] =>
   back(
      item.type === 'tag'
         ? removeTagFromAllGroupsUtil(dnd(groups), item.id)
         : removeStageFromAllGroupsUtil(dnd(groups), item.id)
   )
